// frontend/src/components/Auth/Registration/EmailVerification.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../../api/client';
import './EmailVerification.css';

const EmailVerification = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [step, setStep] = useState('email');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);

    const handleSendCode = async () => {
        if (!email || !email.includes('@')) {
            setError('📧 Please enter a valid email address');
            triggerShake();
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await authAPI.emailVerification({
                action: 'send_code',
                email: email
            });

            if (response.success) {
                setStep('code');
                startResendTimer();
                // Success animation
                const card = document.querySelector('.email-card-gamified');
                if (card) {
                    card.classList.add('success-flash');
                    setTimeout(() => card.classList.remove('success-flash'), 500);
                }
            } else {
                setError(response.message || 'Failed to send verification code');
                triggerShake();
            }
        } catch (err) {
            setError('💀 Network error. Please try again.');
            triggerShake();
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        if (!code || code.length !== 6) {
            setError('🔐 Please enter the 6-digit verification code');
            triggerShake();
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await authAPI.emailVerification({
                action: 'verify_code',
                email: email,
                code: code
            });

            if (response.success) {
                const card = document.querySelector('.email-card-gamified');
                if (card) {
                    card.classList.add('victory-glow');
                }
                setTimeout(() => {
                    navigate('/student-info');
                }, 1000);
            } else {
                setError(response.message || '❌ Invalid verification code');
                triggerShake();
            }
        } catch (err) {
            setError('Network error. Please try again.');
            triggerShake();
        } finally {
            setLoading(false);
        }
    };

    const triggerShake = () => {
        const card = document.querySelector('.email-card-gamified');
        if (card) {
            card.classList.add('game-shake');
            setTimeout(() => card.classList.remove('game-shake'), 400);
        }
    };

    const startResendTimer = () => {
        setResendTimer(60);
        const timer = setInterval(() => {
            setResendTimer(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleResendCode = async () => {
        if (resendTimer > 0) return;

        setLoading(true);
        try {
            const response = await authAPI.emailVerification({
                action: 'send_code',
                email: email
            });
            if (response.success) {
                startResendTimer();
                setError('');
                // Visual feedback
                const resendBtn = document.querySelector('.resend-btn-gamified');
                if (resendBtn) {
                    resendBtn.classList.add('btn-pulse');
                    setTimeout(() => resendBtn.classList.remove('btn-pulse'), 300);
                }
            }
        } catch (err) {
            setError('Failed to resend code');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="email-gamified-world">
            {/* Simple floating particles */}
            <div className="particle-bg-email">
                {[...Array(20)].map((_, i) => (
                    <div key={i} className="particle-email" style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 10}s`,
                        animationDuration: `${8 + Math.random() * 7}s`
                    }}></div>
                ))}
            </div>

            <div className="email-container-gamified">
                <div className="row justify-content-center">
                    <div className="col-lg-7 col-md-9">
                        <div className="email-card-gamified">
                            {/* Header */}
                            <div className="email-header-gamified">
                                <div className="game-badge-email">
                                    <i className="fas fa-dice-d20"></i>
                                    <span>MAGIC SEAL</span>
                                    <i className="fas fa-dice-d6"></i>
                                </div>
                                <div className="header-avatar-email">
                                    <span className="avatar-icon-email">📧</span>
                                    <span className="magic-sparkle">✨</span>
                                </div>
                                <h2>Verify Your Identity</h2>
                                <p>We'll send a magical code to your email</p>
                                <div className="xp-bar-email">
                                    <div className="xp-fill-email" style={{ width: step === 'email' ? '40%' : '60%' }}></div>
                                    <span className="xp-text-email">Step 2 of 5</span>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="email-body-gamified">
                                {error && (
                                    <div className="game-alert-email">
                                        <i className="fas fa-skull me-2"></i>
                                        {error}
                                    </div>
                                )}

                                {step === 'email' ? (
                                    <>
                                        <div className="input-group-email">
                                            <label>
                                                <i className="fas fa-envelope"></i>
                                                <span>EMAIL ADDRESS</span>
                                                <span className="required-badge-email">★ REQUIRED ★</span>
                                            </label>
                                            <div className="input-wrapper-email">
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="hero@example.com"
                                                    autoComplete="email"
                                                />
                                                <div className="input-decoration-email">
                                                    <i className="fas fa-feather"></i>
                                                </div>
                                            </div>
                                            <div className="input-hint-email">
                                                <i className="fas fa-shield-alt"></i>
                                                We'll send a 6-digit magical code to this email
                                            </div>
                                        </div>

                                        <div className="quest-progress-email">
                                            <div className="quest-steps-email">
                                                <div className="step-email completed">🏫</div>
                                                <div className="step-email active">📧</div>
                                                <div className="step-email">🔐</div>
                                                <div className="step-email">👤</div>
                                                <div className="step-email">✅</div>
                                            </div>
                                            <div className="progress-bar-email">
                                                <div className="progress-fill-email" style={{ width: '40%' }}></div>
                                            </div>
                                            <p className="progress-text-email">Quest Progress: 40%</p>
                                        </div>

                                        <button
                                            className="send-btn-gamified"
                                            onClick={handleSendCode}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-email"></span>
                                                    <span>SUMMONING CODE...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane"></i>
                                                    <span>Send Verification Code</span>
                                                    <i className="fas fa-magic"></i>
                                                </>
                                            )}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="info-box-gamified">
                                            <div className="magic-envelope">
                                                <i className="fas fa-envelope-open-text"></i>
                                            </div>
                                            <p>A magical code has been sent to</p>
                                            <strong className="email-highlight">{email}</strong>
                                            <small>Check your inbox & spam folder, hero!</small>
                                        </div>

                                        <div className="input-group-email">
                                            <label>
                                                <i className="fas fa-key"></i>
                                                <span>VERIFICATION CODE</span>
                                                <span className="required-badge-email legendary">⚡ 6 DIGITS ⚡</span>
                                            </label>
                                            <div className="input-wrapper-email">
                                                <input
                                                    type="text"
                                                    value={code}
                                                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                                                    placeholder="000000"
                                                    maxLength="6"
                                                    className="code-input"
                                                />
                                                <div className="input-decoration-email">
                                                    <i className="fas fa-lock"></i>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            className="verify-btn-gamified"
                                            onClick={handleVerifyCode}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-email"></span>
                                                    <span>VERIFYING...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-check-circle"></i>
                                                    <span>Verify & Continue →</span>
                                                    <i className="fas fa-crown"></i>
                                                </>
                                            )}
                                        </button>

                                        <div className="resend-section-gamified">
                                            {resendTimer > 0 ? (
                                                <div className="timer-gamified">
                                                    <i className="fas fa-hourglass-half"></i>
                                                    <span>Resend in {resendTimer}s</span>
                                                </div>
                                            ) : (
                                                <button className="resend-btn-gamified" onClick={handleResendCode}>
                                                    <i className="fas fa-redo-alt"></i>
                                                    <span>Resend Magical Code</span>
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Back to previous */}
                                {step === 'code' && (
                                    <div className="back-link-email">
                                        <button className="back-btn-email" onClick={() => setStep('email')}>
                                            <i className="fas fa-arrow-left"></i>
                                            <span>Wrong email? Go back</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;